import {
  Shield,
  Cloud,
  Mail,
  Monitor,
  Globe,
  Key,
  Activity,
  Server,
  Building2,
} from "lucide-react";
import type { Integration, IntegrationSourceType } from "@/types/api";

/**
 * Presentation metadata for integration source types and health.
 *
 * Extracted from the old card UI so the integrations data grid (#208) and the
 * per-cloud onboarding wizards (#176/#177/#178) share one source of truth for
 * icons and labels.
 */

type IconComponent = React.ComponentType<{ className?: string }>;

export const SOURCE_TYPE_ICONS: Record<IntegrationSourceType, IconComponent> = {
  entra_id: Key,
  crowdstrike: Shield,
  sentinel: Shield,
  m365_defender: Mail,
  cloudtrail: Cloud,
  azure_activity: Activity,
  gcp_audit: Cloud,
  google_workspace: Building2,
  m365: Mail,
  mimecast_email: Mail,
  endpoint: Monitor,
  firewall: Globe,
};

export const SOURCE_TYPE_LABELS: Record<IntegrationSourceType, string> = {
  entra_id: "Microsoft Entra ID",
  crowdstrike: "CrowdStrike",
  sentinel: "Microsoft Sentinel",
  m365_defender: "Microsoft 365 Defender",
  cloudtrail: "AWS CloudTrail",
  azure_activity: "Azure Activity Log",
  gcp_audit: "Google Cloud Audit Log",
  google_workspace: "Google Workspace",
  m365: "Microsoft 365",
  mimecast_email: "Mimecast Email Security",
  endpoint: "Endpoint Agent",
  firewall: "Firewall / Network",
};

/** Icon for a source type, falling back to a generic server glyph. */
export function sourceTypeIcon(t: IntegrationSourceType): IconComponent {
  return SOURCE_TYPE_ICONS[t] ?? Server;
}

/** Human label for a source type, falling back to the raw value. */
export function sourceTypeLabel(t: IntegrationSourceType | string): string {
  return SOURCE_TYPE_LABELS[t as IntegrationSourceType] ?? t;
}

export type HealthStatus = Integration["health_status"];

export const HEALTH_CONFIG: Record<
  HealthStatus,
  { dot: string; label: string; badge: string }
> = {
  healthy: {
    dot: "bg-green-500",
    label: "Healthy",
    badge: "border-green-500/30 bg-green-500/10 text-green-400",
  },
  unhealthy: {
    dot: "bg-red-500",
    label: "Unhealthy",
    badge: "border-red-500/30 bg-red-500/10 text-red-400",
  },
  unknown: {
    dot: "bg-gray-500",
    label: "Unknown",
    badge: "border-gray-500/30 bg-gray-500/10 text-gray-400",
  },
  disabled: {
    dot: "bg-yellow-500",
    label: "Disabled",
    badge: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
  },
};

/** Display config for user-paused integrations (user_enabled = false). */
export const PAUSED_CONFIG = {
  dot: "bg-gray-400",
  label: "Paused",
  badge: "border-gray-400/30 bg-gray-400/10 text-gray-400",
};

/**
 * Returns the display config for an integration's status badge.
 * user_enabled = false takes precedence: show "Paused" regardless of health_status.
 */
export function getDisplayConfig(
  integration: Integration,
): { dot: string; label: string; badge: string } {
  if (!integration.user_enabled) return PAUSED_CONFIG;
  return HEALTH_CONFIG[integration.health_status] ?? HEALTH_CONFIG.unknown;
}

/**
 * Sort rank for effective display status: lower = more attention needed.
 * unhealthy(0) → unknown(1) → paused(2) → disabled(3) → healthy(4)
 */
export function effectiveRank(integration: Integration): number {
  if (!integration.user_enabled) return 2;
  const RANK: Record<HealthStatus, number> = {
    unhealthy: 0,
    unknown: 1,
    disabled: 3,
    healthy: 4,
  };
  return RANK[integration.health_status] ?? 99;
}

/**
 * Sort rank for health status alone (no user_enabled awareness).
 * @deprecated Use effectiveRank() for integrations with user_enabled.
 */
const HEALTH_RANK: Record<HealthStatus, number> = {
  unhealthy: 0,
  unknown: 1,
  disabled: 2,
  healthy: 3,
};

export function healthRank(s: HealthStatus): number {
  return HEALTH_RANK[s] ?? 99;
}
